import type { PropertyMapping } from '~/services/AccommodationService';

import { isPropertyNotMappedError } from '~/utils/accommodationPropertyImageSync';
import { reportError } from '~/utils/reportError';

import { resolveRoomNameMatchesWithAi } from '../llmRoomNameMatcher';

import type { FlashOfferRoomDraft } from './utils';

export type AccommodationLinkStatus = 'failed' | 'linked' | 'unlinked';

export interface RoomAmenityPrefill {
  linkStatus: AccommodationLinkStatus;
  /** Draft id -> the accommodation room its amenities came from, or null when none could be used. */
  sourceRoomNameByDraftId: Record<string, string | null>;
}

export interface RoomAmenityLookup {
  fetchApprovedRoomAmenityNames: (accommodationRoomId: string) => Promise<Array<string>>;
  fetchPropertyMapping: (reservationPropertyId: string) => Promise<PropertyMapping>;
}

interface PrefillRoomAmenitiesParams {
  lookup: RoomAmenityLookup;
  reservationPropertyId: string;
  roomDrafts: Array<FlashOfferRoomDraft>;
}

interface PrefillRoomAmenitiesResult {
  prefill: RoomAmenityPrefill;
  roomDrafts: Array<FlashOfferRoomDraft>;
}

function withoutPrefill(
  linkStatus: AccommodationLinkStatus,
  roomDrafts: Array<FlashOfferRoomDraft>,
): PrefillRoomAmenitiesResult {
  return { prefill: { linkStatus, sourceRoomNameByDraftId: {} }, roomDrafts };
}

// A room already linked to a reservation room type would have matched an existing room type instead.
function getUnlinkedAccommodationRooms(propertyMapping: PropertyMapping) {
  return Object.values(propertyMapping.rooms ?? {}).filter(
    (room) => !room.isMysteryRoom && !room.reservationIds?.length,
  );
}

export async function prefillRoomAmenities({
  lookup,
  reservationPropertyId,
  roomDrafts,
}: PrefillRoomAmenitiesParams): Promise<PrefillRoomAmenitiesResult> {
  let propertyMapping: PropertyMapping;

  try {
    propertyMapping = await lookup.fetchPropertyMapping(reservationPropertyId);
  } catch (error) {
    if (isPropertyNotMappedError(error)) {
      return withoutPrefill('unlinked', roomDrafts);
    }

    reportError(error);
    return withoutPrefill('failed', roomDrafts);
  }

  const accommodationRooms = getUnlinkedAccommodationRooms(propertyMapping);
  const { hasFailed, matches } = await resolveRoomNameMatchesWithAi(
    roomDrafts.map((draft) => draft.name),
    accommodationRooms.map((room) => room.name),
  );

  if (hasFailed) {
    return withoutPrefill('failed', roomDrafts);
  }

  const accommodationRoomByName = new Map(accommodationRooms.map((room) => [room.name, room]));
  const sourceRoomNameByDraftId: Record<string, string | null> = {};

  const prefilledRoomDrafts = await Promise.all(
    roomDrafts.map(async (draft) => {
      const matchedRoomName = matches[draft.name];
      const accommodationRoom = matchedRoomName ? accommodationRoomByName.get(matchedRoomName) : undefined;
      let amenityNames: Array<string> = [];

      if (accommodationRoom) {
        try {
          amenityNames = await lookup.fetchApprovedRoomAmenityNames(accommodationRoom.id);
        } catch (error) {
          reportError(error);
        }
      }

      if (!accommodationRoom || amenityNames.length === 0) {
        sourceRoomNameByDraftId[draft.draftId] = null;
        return draft;
      }

      sourceRoomNameByDraftId[draft.draftId] = accommodationRoom.name;
      // Several catalog codes can share a display name (e.g. three "TV"s).
      return { ...draft, amenities: Array.from(new Set(amenityNames)) };
    }),
  );

  return { prefill: { linkStatus: 'linked', sourceRoomNameByDraftId }, roomDrafts: prefilledRoomDrafts };
}

export function getRoomAmenityPrefillNote(prefill: RoomAmenityPrefill | null, draftId: string) {
  if (prefill?.linkStatus !== 'linked' || !(draftId in prefill.sourceRoomNameByDraftId)) {
    return undefined;
  }

  const sourceRoomName = prefill.sourceRoomNameByDraftId[draftId];

  return sourceRoomName
    ? `Prefilled from the accommodation room "${sourceRoomName}". Review before submitting.`
    : "Couldn't prefill amenities from the accommodation listing. Pick them here, or sync them later in Content → Rooms.";
}
